/**
 * BudgetBanner — баннер соответствия суммы траншей бюджету сделки (данные из GET /stats).
 *   Σ == price      → зелёный «Сумма траншей совпадает с бюджетом — N ₽» + активная «Завершить настройку».
 *   Σ  < price       → жёлтый «недобор на N ₽».
 *   Σ  > price       → красный «превышение на N ₽».
 * Кнопка «Завершить настройку» (POST /finalize, право manage_settings) активна только при budgetMatches.
 *
 * FinalizeButton экспортируется отдельно, но по макету живёт внутри баннера (зелёная ветка).
 */

import { useState } from 'react';
import type { StatsDto } from '../../lib/types';
import { formatRub, subMinor } from '../../lib/money';
import { cx } from './ui/cx';
import { Button } from './ui/Button';
import { IconCheck, IconWarn } from './ui/icons';
import { useFinalize, type DealScope } from './api';
import { ApiError } from '../../lib/apiClient';

export interface FinalizeButtonProps {
  scope: DealScope;
  disabled?: boolean;
}

export function FinalizeButton({ scope, disabled }: FinalizeButtonProps): JSX.Element {
  const finalize = useFinalize(scope);
  const [error, setError] = useState<string | null>(null);

  return (
    <span className="pw-finalize">
      <Button
        variant="primary"
        size="sm"
        disabled={disabled || finalize.isPending}
        onClick={() => {
          setError(null);
          finalize.mutate(undefined, {
            onError: (e) => {
              setError(
                e instanceof ApiError && e.isBudgetMismatch
                  ? 'Сумма траншей разошлась с бюджетом — данные обновлены.'
                  : 'Не удалось завершить настройку.',
              );
            },
          });
        }}
      >
        {finalize.isPending ? 'Завершаем…' : 'Завершить настройку'}
      </Button>
      {error ? (
        <span className="pw-finalize__err" role="alert">
          {error}
        </span>
      ) : null}
    </span>
  );
}

export interface BudgetBannerProps {
  scope: DealScope;
  stats: StatsDto;
  /** Показывать активную кнопку завершения (право manage_settings). */
  canManageSettings: boolean;
}

export function BudgetBanner({ scope, stats, canManageSettings }: BudgetBannerProps): JSX.Element {
  const matches = stats.budgetMatches;
  // diff = план − бюджет; >0 превышение, <0 недобор.
  const diffMinor = subMinor(stats.plannedTotalMinor, stats.totalBudgetMinor);
  const over = BigInt(diffMinor) > 0n;
  const absDiff = (BigInt(diffMinor) < 0n ? -BigInt(diffMinor) : BigInt(diffMinor)).toString();

  const tone = matches ? 'success' : over ? 'danger' : 'warning';

  return (
    <div className={cx('pw-banner', `pw-banner--${tone}`)} role="status">
      <span className="pw-banner__icon" aria-hidden>
        {matches ? <IconCheck size={18} /> : <IconWarn size={18} />}
      </span>
      <div className="pw-banner__body">
        {matches ? (
          <span>
            Сумма траншей совпадает с бюджетом — <strong className="pw-num">{formatRub(stats.totalBudgetMinor)}</strong>.
          </span>
        ) : (
          <span>
            Сумма траншей не совпадает с бюджетом: запланировано{' '}
            <strong className="pw-num">{formatRub(stats.plannedTotalMinor)}</strong> из{' '}
            <strong className="pw-num">{formatRub(stats.totalBudgetMinor)}</strong> —{' '}
            {over ? 'превышение' : 'недобор'} на <strong className="pw-num">{formatRub(absDiff)}</strong>.
          </span>
        )}
      </div>
      {canManageSettings && !stats.scheduleComplete ? (
        <div className="pw-banner__action">
          <FinalizeButton scope={scope} disabled={!matches} />
        </div>
      ) : null}
    </div>
  );
}
